"use client";

import { Star, Users, Presentation, Heart, Gift, ChevronLeft, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState, useEffect } from "react";
const DONATION_URL = "https://securelb.imodules.com/s/699/bp19/interior.aspx?sid=699&gid=1&sitebuilder=1&pgid=961&cid=2272&bledit=1&dids=911.&appealcode=A3707&sort=1";

// Match the example background
const POLY_GREEN = "#154734";
const sponsors = [{
  id: 1,
  name: "TurboRad",
  logo: "https://eilhconference.org/wp-content/uploads/2026/03/1755713076548-Photoroom.png",
  description: "TurboRad is revolutionizing medical imaging with cutting-edge AI technology. Their platform streamlines radiology workflows, enhances diagnostic accuracy, and improves patient outcomes through intelligent automation and advanced image analysis.",
  website: "https://www.turborad.com/"
}, {
  id: 2,
  name: "Radiology Associates",
  logo: "https://eilhconference.org/wp-content/uploads/2026/03/Radiology-Associates-Logo-v1.png",
  description: "Radiology Associates provides comprehensive diagnostic imaging services across multiple locations. With expertise in Digital Medical Imaging (DMI), Five Cities Medical Imaging, and Radiology Diagnostic Center (RDC), they deliver excellence in patient care and medical imaging.",
  website: "https://rasloimaging.com/"
}, {
  id: 3,
  name: "IME Department",
  logo: "https://eilhconference.org/wp-content/uploads/2026/03/IME-department-logo-Picsart-BackgroundRemover.png",
  description: "The Industrial and Manufacturing Engineering Department at Cal Poly prepares students to improve systems, solve real-world problems, and lead with practical experience. Through hands-on learning, IME students develop the skills to design smarter processes and make a meaningful impact across industries.",
  website: "https://ime.calpoly.edu/"
}];
const sponsorshipTiers = [{
  id: 1,
  name: "Named Conference Sponsor",
  price: "$10,000",
  icon: Star,
  gradient: "from-yellow-600/30 to-amber-600/30",
  borderColor: "border-yellow-500/50",
  hoverBorder: "hover:border-yellow-400",
  benefits: ["Named Conference Sponsor recognition", "Premier logo placement across conference materials and website", "Verbal recognition during opening and closing sessions", "Opportunity for brief sponsor acknowledgment", "Post-conference recognition"]
}, {
  id: 2,
  name: "Reception Sponsor",
  price: "$5,000",
  icon: Users,
  gradient: "from-orange-600/30 to-red-600/30",
  borderColor: "border-orange-500/50",
  hoverBorder: "hover:border-orange-400",
  benefits: ["Recognition as the Official Conference Reception Sponsor", "Logo placement at the reception and on the conference website", "Verbal acknowledgment during the reception", "Recognition in select conference materials"]
}, {
  id: 3,
  name: "Session Sponsor",
  price: "$1,000",
  icon: Presentation,
  gradient: "from-blue-600/30 to-cyan-600/30",
  borderColor: "border-blue-500/50",
  hoverBorder: "hover:border-blue-400",
  benefits: ["Recognition as a Session Sponsor for a designated panel or session", "Logo placement on the sponsored session slide and conference website", "Verbal acknowledgment at the start of the sponsored session"]
}, {
  id: 4,
  name: "Supporting Sponsor",
  price: "$500",
  icon: Heart,
  gradient: "from-purple-600/30 to-pink-600/30",
  borderColor: "border-purple-500/50",
  hoverBorder: "hover:border-purple-400",
  benefits: ["Recognition as a Supporting Sponsor", "Logo or organization name listed on the conference website and select materials"]
}, {
  id: 5,
  name: "In-Kind Sponsorship",
  price: "Open",
  icon: Gift,
  gradient: "from-teal-600/30 to-green-600/30",
  borderColor: "border-teal-500/50",
  hoverBorder: "hover:border-teal-400",
  benefits: ["Support the conference through in-kind contributions: catering, technology support, materials, or promotional items", "Recognition aligned with the value and nature of the contribution"]
}];
function SponsorsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % sponsors.length);
    }, 5000); // 5 seconds per sponsor

    return () => clearInterval(interval);
  }, [isAutoPlaying]);
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume autoplay after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  const goToPrevious = () => {
    setCurrentIndex(prev => (prev - 1 + sponsors.length) % sponsors.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % sponsors.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  const currentSponsor = sponsors[currentIndex];
  return <div className="relative w-full">
      {/* Header */}
      <h2 className="text-5xl font-bold text-center mb-16 px-4 tracking-normal !text-white uppercase leading-none !font-serif sm:text-6xl md:text-7xl sm:tracking-wider">SPONSORS</h2>

      {/* Carousel Content */}
      <div className="relative max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 items-center min-h-[400px]">
          {/* Logo Card - Left Side */}
          <div className="relative">
            <a href={currentSponsor.website} target="_blank" rel="noopener noreferrer" className="block mx-auto w-full max-w-[425px] aspect-square bg-white/5 backdrop-blur-sm rounded-2xl border-2 border-white/20 p-12 flex items-center justify-center transition-all duration-500 hover:border-[hsl(49,100%,57%)]/50 hover:shadow-2xl cursor-pointer">
              <div className="w-full h-full flex items-center justify-center">
                <img src={currentSponsor.logo} alt={currentSponsor.name} className="max-w-full max-h-full object-contain" />
              </div>
            </a>
          </div>

          {/* Description - Right Side */}
          <div className="flex flex-col justify-center space-y-6 px-4">
            <div className="space-y-4">
              <h3 className="text-3xl font-bold text-[hsl(49,100%,57%)] mb-4">
                {currentSponsor.name}
              </h3>
              <p className="text-white/90 text-lg leading-relaxed font-light tracking-wide">
                {currentSponsor.description}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-center gap-6 mt-12">
          {/* Previous Button */}
          <button onClick={goToPrevious} className="p-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-300 hover:scale-110" aria-label="Previous sponsor">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          {/* Dots Indicator */}
          <div className="flex gap-3">
            {sponsors.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`transition-all duration-300 rounded-full ${index === currentIndex ? "w-12 h-3 bg-white" : "w-3 h-3 bg-white/40 hover:bg-white/60"}`} aria-label={`Go to sponsor ${index + 1}`} />)}
          </div>

          {/* Next Button */}
          <button onClick={goToNext} className="p-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-300 hover:scale-110" aria-label="Next sponsor">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Sponsor Logos Footer */}
      <div className="mt-16 pt-8 border-t border-white/10">
        <div className="flex flex-wrap items-center justify-center gap-12">
          {sponsors.map(sponsor => <a key={sponsor.id} href={sponsor.website} target="_blank" rel="noopener noreferrer" className="transition-all duration-300 hover:scale-110 cursor-pointer">
              <img src={sponsor.logo} alt={sponsor.name} className="h-16 md:h-20 object-contain opacity-70 hover:opacity-100 transition-opacity" />
            </a>)}
        </div>
      </div>
    </div>;
}
export default function BecomeASponsor() {
  const header = useRevealOnScroll({
    threshold: 0.2
  });
  const buttonRef = useRevealOnScroll({
    threshold: 0.2
  });
  const tiersRef = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="become-a-sponsor" className="relative py-24 px-8 overflow-hidden" style={{
    backgroundColor: POLY_GREEN
  }}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="max-w-7xl mx-auto space-y-24 relative">
        {/* NEW: Sponsors Carousel Section */}
        <div className="pb-16 border-b border-white/10">
          <SponsorsCarousel />
        </div>

        {/* Header */}
        <div ref={header.ref} className={`text-center space-y-6 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-bold text-5xl leading-tight !font-serif !text-[hsl(0,0%,100%)] md:text-6xl">
            Become a Sponsor
          </h2>
          <p className="text-foreground-primary text-xl max-w-4xl mx-auto leading-relaxed">
            This conference brings together AI experts, engineers, and medical students to explore how artificial
            intelligence is shaping the future of healthcare. Partner with us to support interdisciplinary
            collaboration, ethical innovation, and leadership development.
          </p>
          <p className="text-accent font-semibold text-lg">
            We invite you to partner with us through the following sponsorship opportunities:
          </p>
        </div>

        {/* Animated Donate Button with Arrows */}
        <div ref={buttonRef.ref} className={`relative flex justify-center py-20 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${buttonRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Top Left Arrow */}
          <svg className="absolute top-2 left-2 sm:top-4 sm:left-[10%] md:left-[15%] lg:left-[22%] xl:left-[28%]
                       w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                       text-[hsl(49,100%,57%)] animate-bounce-arrow pointer-events-none" style={{
          animationDelay: "0s"
        }} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <marker id="arrowhead-tl" markerWidth="5" markerHeight="5" refX="7" refY="5" orient="auto" markerUnits="strokeWidth" viewBox="0 0 10 10">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
            <path d="M 15 15 Q 35 30, 60 55" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" markerEnd="url(#arrowhead-tl)" />
          </svg>

          {/* Top Right Arrow */}
          <svg className="absolute top-2 right-2 sm:top-4 sm:right-[10%] md:right-[15%] lg:right-[22%] xl:right-[28%]
                       w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                       text-[hsl(49,100%,57%)] animate-bounce-arrow pointer-events-none" style={{
          animationDelay: "0.5s"
        }} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <marker id="arrowhead-tr" markerWidth="5" markerHeight="5" refX="7" refY="5" orient="auto" markerUnits="strokeWidth" viewBox="0 0 10 10">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
            <path d="M 85 15 Q 65 30, 40 55" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" markerEnd="url(#arrowhead-tr)" />
          </svg>

          {/* Bottom Left Arrow */}
          <svg className="absolute bottom-2 left-2 sm:bottom-4 sm:left-[10%] md:left-[15%] lg:left-[22%] xl:left-[28%]
                       w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                       text-[hsl(49,100%,57%)] animate-bounce-arrow pointer-events-none" style={{
          animationDelay: "1s"
        }} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <marker id="arrowhead-bl" markerWidth="5" markerHeight="5" refX="7" refY="5" orient="auto" markerUnits="strokeWidth" viewBox="0 0 10 10">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
            <path d="M 15 85 Q 35 70, 60 45" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" markerEnd="url(#arrowhead-bl)" />
          </svg>

          {/* Bottom Right Arrow */}
          <svg className="absolute bottom-2 right-2 sm:bottom-4 sm:right-[10%] md:right-[15%] lg:right-[22%] xl:right-[28%]
                       w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                       text-[hsl(49,100%,57%)] animate-bounce-arrow pointer-events-none" style={{
          animationDelay: "1.5s"
        }} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <marker id="arrowhead-br" markerWidth="5" markerHeight="5" refX="7" refY="5" orient="auto" markerUnits="strokeWidth" viewBox="0 0 10 10">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
            <path d="M 85 85 Q 65 70, 40 45" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" markerEnd="url(#arrowhead-br)" />
          </svg>

          {/* Donate Button */}
          <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center rounded-full px-12 py-6 font-bold text-2xl transition-all duration-300 !text-[hsl(0,0%,0%)] !bg-[hsl(49,100%,57%)] hover:scale-110 hover:shadow-2xl animate-pulse-slow overflow-hidden z-10">
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <span className="relative z-10 group-hover:scale-110 transition-transform duration-300">
              Donate Now!
            </span>
            <span className="absolute inset-0 rounded-full bg-[hsl(49,100%,57%)] opacity-30 animate-ping-slow" />
          </a>
        </div>

        {/* Sponsorship Tiers Grid */}
        <div ref={tiersRef.ref} className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${tiersRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {sponsorshipTiers.map((tier, index) => {
          const Icon = tier.icon;
          return <a key={tier.id} href={DONATION_URL} target="_blank" rel="noopener noreferrer" className={`relative group rounded-xl border-2 ${tier.borderColor} ${tier.hoverBorder} bg-gradient-to-br ${tier.gradient} p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer ${index === 0 ? "md:col-span-2 lg:col-span-1" : ""}`} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                {/* Icon */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-full bg-accent/20 group-hover:bg-accent/30 transition-colors">
                    <Icon className="w-6 h-6 text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-foreground-primary font-bold text-2xl">{tier.name}</h3>
                    <p className="text-accent text-3xl font-bold mt-1">{tier.price}</p>
                  </div>
                </div>

                {/* Benefits */}
                <ul className="space-y-3 mt-6">
                  {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-3 text-foreground-primary">
                      <span className="text-accent mt-1 text-xl">•</span>
                      <span className="text-sm leading-relaxed">{benefit}</span>
                    </li>)}
                </ul>

                {/* Click to donate indicator */}
                <div className="mt-6 pt-4 border-t border-white/10">
                  <p className="text-accent text-sm font-semibold text-center group-hover:underline">
                    Click to Donate →
                  </p>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 rounded-xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </a>;
        })}
        </div>

        {/* Bottom Message */}
        <div className="text-center py-8 px-6 bg-accent/10 rounded-lg border border-accent/30">
          <p className="text-foreground-primary text-lg font-semibold">
            Sponsorships contribute to creating an accessible, high-quality learning experience for emerging leaders
            at the intersection of AI and healthcare.
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.85;
          }
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.1);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        @keyframes bounce-arrow {
          0%,
          100% {
            transform: translateY(0) scale(1);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-10px) scale(1.05);
            opacity: 1;
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        .animate-bounce-arrow {
          animation: bounce-arrow 2s ease-in-out infinite;
        }
      `}</style>
    </section>;
}